import {
  BadRequestException,
  Body,
  Controller,
  Headers,
  Post,
  RawBodyRequest,
  Req,
  UseGuards,
} from '@nestjs/common';
import { ApiBody, ApiOperation, ApiResponse, ApiSecurity, ApiTags } from '@nestjs/swagger';
import { Request } from 'express';
import { GuardedRequest, AuthGuard } from '../auth/auth.guard';
import { RazorpayService } from './razorpay.service';
import { OrderSummary } from '@cart/shared-types';

@ApiTags('Payments')
@ApiSecurity('store-id')
@Controller('payments/razorpay')
export class RazorpayController {
  constructor(private readonly razorpay: RazorpayService) {}

  /**
   * Step 1 — Create a Razorpay order for the open checkout session.
   * Returns the razorpayOrderId + keyId that the frontend needs to open the popup.
   */
  @Post('create-order')
  @UseGuards(AuthGuard)
  @ApiOperation({ summary: 'Create a Razorpay order for the current checkout session' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['checkoutToken'],
      properties: {
        checkoutToken: { type: 'string', example: 'checkout-1234-abcd' },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Returns razorpayOrderId, amount, currency, keyId' })
  createOrder(
    @Req() req: GuardedRequest,
    @Body() body: { checkoutToken: string },
  ) {
    return this.razorpay.createOrder(req.user.id, body.checkoutToken);
  }

  /**
   * Step 2 — Verify Razorpay payment signature + complete the checkout session.
   * Returns the created OrderSummary on success.
   */
  @Post('verify')
  @UseGuards(AuthGuard)
  @ApiOperation({ summary: 'Verify payment and complete checkout session' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['checkoutToken', 'razorpayOrderId', 'razorpayPaymentId', 'razorpaySignature'],
      properties: {
        checkoutToken:      { type: 'string' },
        razorpayOrderId:    { type: 'string', example: 'order_xyz' },
        razorpayPaymentId:  { type: 'string', example: 'pay_abc' },
        razorpaySignature:  { type: 'string' },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Payment verified — returns OrderSummary' })
  @ApiResponse({ status: 400, description: 'Signature verification failed' })
  verify(
    @Req() req: GuardedRequest,
    @Body() body: {
      checkoutToken: string;
      razorpayOrderId: string;
      razorpayPaymentId: string;
      razorpaySignature: string;
    },
  ): Promise<OrderSummary> {
    return this.razorpay.verifyAndComplete(req.user.id, {
      checkoutToken:     body.checkoutToken,
      razorpayOrderId:   body.razorpayOrderId,
      razorpayPaymentId: body.razorpayPaymentId,
      razorpaySignature: body.razorpaySignature,
    });
  }

  /**
   * Webhook endpoint — Razorpay sends async payment events here.
   * Must be reachable from the internet (use ngrok in dev).
   * No AuthGuard — verified by HMAC signature instead.
   */
  @Post('webhook')
  @ApiOperation({ summary: 'Razorpay webhook receiver' })
  @ApiResponse({ status: 200, description: '{ ok: true }' })
  webhook(
    @Req() req: RawBodyRequest<Request>,
    @Headers('x-razorpay-signature') signature: string,
  ) {
    if (!req.rawBody) {
      // rawBody is populated by the rawBody bodyParser option (main.ts). If it
      // is missing the signature cannot be verified, and verifying against a
      // re-serialised body would accept a tampered payload.
      throw new BadRequestException('Webhook body could not be read');
    }
    return this.razorpay.handleWebhook(req.rawBody, signature);
  }
}
